import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular-ivy';
import { lastValueFrom } from 'rxjs';

import { ApiService } from 'src/app/services/api.service';
import { CompanySettingsChanged } from 'src/app/store/auth/auth.actions';
import { AuthState } from 'src/app/store/auth/auth.state';

const TTL_MS = 24 * 60 * 60 * 1000;

interface SyncIdsResponse {
  data: {
    status: 'success' | 'skipped';
    contactId?: string;
    attempts?: number;
    hubspotIdsSyncedAt?: string;
    reason?: string;
  };
}

/**
 * Ensures the current company's HubSpot contact has `tdcompanyid` / `tduserid`
 * patched before downstream flows (billing-api cancel-subscription) rely on them.
 *
 * The actual HubSpot CRM PATCH happens server-side in api-server
 * (`POST companies/{id}/hubspot/sync-ids`), which persists `hubspotContactId`
 * and `hubspotIdsSyncedAt` under `settings.custom` on the company record.
 * No separate `putCompanySettings` call is needed — the TTL gate survives hard
 * refreshes because company settings are loaded from the backend on login.
 *
 * This client service orchestrates a TTL-aware call, updates NGXS in-memory so
 * the same session can short-circuit without another round-trip, and logs
 * failures to Sentry without ever blocking the caller.
 */
@Injectable({
  providedIn: 'root',
})
export class HubspotContactSyncService {
  private inflight: Promise<void> | null = null;

  constructor(
    private api: ApiService,
    private store: Store,
  ) { }

  ensureSyncedForCancellation(): Promise<void> {
    return this.ensureSynced('billing-cancel-flow');
  }

  private ensureSynced(flowContext: string): Promise<void> {
    if (this.inflight !== null) { return this.inflight; }
    this.inflight = this.run(flowContext).finally(() => { this.inflight = null; });
    return this.inflight;
  }

  private async run(flow: string): Promise<void> {
    const company = this.store.selectSnapshot(AuthState.company);
    const user = this.store.selectSnapshot(AuthState.user);

    if (!company?.id || !user?.id) { return; }

    const syncedAt = company.companySettings?.custom?.hubspotIdsSyncedAt;
    if (syncedAt && Date.now() - new Date(syncedAt).getTime() < TTL_MS) {
      return;
    }

    try {
      const response = await lastValueFrom(
        this.api.request<SyncIdsResponse>('post', `companies/${company.id}/hubspot/sync-ids`, {}),
      );

      if (response?.data?.status !== 'success') { return; }

      // In-session cache only — api-server already persisted these fields.
      this.store.dispatch(new CompanySettingsChanged({}, {}, {
        ...(response.data.contactId ? { hubspotContactId: response.data.contactId } : {}),
        hubspotIdsSyncedAt: response.data.hubspotIdsSyncedAt || new Date().toISOString(),
      }));
    } catch (error) {
      this.captureSyncFailure(error, flow);
    }
  }

  private captureSyncFailure(error: unknown, flow: string): void {
    const httpError = error instanceof HttpErrorResponse ? error : null;
    const rawBody = httpError?.error;
    const errorCode = rawBody !== null && typeof rawBody === 'object' && 'errorCode' in rawBody
      && typeof (rawBody as Record<string, unknown>).errorCode === 'string'
      ? (rawBody as Record<string, unknown>).errorCode as string
      : undefined;

    Sentry.captureException(error instanceof Error ? error : new Error('HubSpot contact sync failed'), {
      tags: { flow, endpoint: 'companies/hubspot/sync-ids' },
      extra: {
        hubspotStatus: httpError?.status,
        ...(errorCode !== undefined ? { hubspotErrorCode: errorCode } : {}),
      },
    });
  }
}
